import React, { useState } from 'react';
import { Leaf, Upload, ArrowRight, ArrowDown, X, Activity } from 'lucide-react';
import Card from '../components/ui/Card';
import ApiService from '../services/apiService';
import { openImageInNewTab, fileToBase64 } from '../utils/helpers';
import { commitModule } from '../utils/reportMarkers';
import { capDataUrl, MAX_IMAGE_EDGE_PX, MAX_IMAGE_BYTES } from '../utils/imageGuards';

const LeafDoctorView = ({ t, userId, onDiagnose, setReportData }) => {
  const [plantType, setPlantType] = useState('general'); // 'general' or 'palm'
  const [items, setItems] = useState([]); // Array of { file, preview, status, result, glowImage }
  const [isProcessing, setIsProcessing] = useState(false);

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length > 0) {
      const newItems = selectedFiles.map(file => ({
        id: Math.random().toString(36).substr(2, 9),
        file,
        preview: URL.createObjectURL(file),
        status: 'pending', // 'pending', 'processing', 'success', 'error'
        result: null,
        glowImage: null,
        errorMsg: null
      }));
      setItems(prev => [...prev, ...newItems]);
    }
  };

  const handleReset = () => {
    setItems([]);
    setIsProcessing(false);
  };

  // Writes the report's leaf section from a LOCAL outcome list: the diagnosis, the user's
  // uploaded photo and the segmentation mask, plus the 'leaf' run marker -- all in ONE state
  // update (contract 6.4 / 1.3). Nothing here reads the `items` state value, which is stale for
  // the whole of processImages because setItems is asynchronous (contract 1.4).
  const commitLeafReport = async (outcomes) => {
    if (!setReportData || !outcomes.length) return;

    // Prefer the first image that actually diagnosed; otherwise report the first failure.
    const chosen = outcomes.find(o => o.ok) || outcomes[0];
    const ok = Boolean(chosen.ok && chosen.result);

    // The uploaded photo has to reach the PDF as a real base64 data URL -- `item.preview` is an
    // object URL and the renderer cannot resolve it. Re-read the File and cap it so one large
    // phone photo cannot blow the payload budget.
    let inputImage = null;
    try {
      const rawInput = chosen.file ? await fileToBase64(chosen.file) : null;
      if (rawInput) {
        inputImage = await capDataUrl(rawInput, {
          maxEdge: MAX_IMAGE_EDGE_PX,
          maxBytes: MAX_IMAGE_BYTES
        });
      }
    } catch (err) {
      inputImage = null;
    }

    let maskImage = null;
    try {
      if (chosen.glowImage) {
        maskImage = await capDataUrl(chosen.glowImage, {
          maxEdge: MAX_IMAGE_EDGE_PX,
          maxBytes: MAX_IMAGE_BYTES
        });
      }
    } catch (err) {
      maskImage = null;
    }

    const rawConfidence = Number(chosen.result && chosen.result.confidence);
    const confidence = Number.isFinite(rawConfidence) ? Math.round(rawConfidence * 100) / 100 : 0;

    commitModule(setReportData, 'leaf', {
      leaf_diagnosis: {
        // Verbatim backend string -- the export modal's disease lookup depends on it.
        diagnosis: (chosen.result && chosen.result.diagnosis) || '',
        confidence,
        leaves_detected: chosen.leavesDetected,
        mask_image_base64: maskImage
      },
      leaf_input_image_base64: inputImage
    }, ok, ok ? null : String(chosen.error || 'Leaf analysis failed').slice(0, 200));
  };

  const processImages = async () => {
    setIsProcessing(true);

    // Local record of this run. setItems() below is asynchronous, so `items` stays at its
    // render-time value for the rest of this handler and must not be read back.
    const outcomes = [];

    for (let i = 0; i < items.length; i++) {
      if (items[i].status !== 'pending') continue;

      const sourceFile = items[i].file;

      // Update current item to processing
      setItems(prev => prev.map((item, idx) => idx === i ? { ...item, status: 'processing' } : item));

      const formData = new FormData();
      formData.append("file", sourceFile);
      if (userId) formData.append("user_id", userId);

      try {
        let finalResult = null;
        let glowImage = null;
        let leavesDetected = null;

        if (plantType === 'palm') {
          // Feature 1: Palm Disease Flow
          const json = await ApiService.post('/diagnose-palm-disease', formData, true);
          window.dispatchEvent(new CustomEvent('credit-deducted', { detail: { amount: 5 } }));

          if (json.status === "error") throw new Error(t.leaf.noLeaves);

          finalResult = { diagnosis: json.diagnosis, confidence: json.diagnosis_confidence };
          glowImage = json.isolated_mask_image;

        } else {
          // Feature 2: General Leaf Flow (Segment First, then Classify)
          const segJson = await ApiService.post('/segment-general-leaf', formData, true);

          if (segJson.status === "error") throw new Error(t.leaf.noLeaves);
          glowImage = segJson.isolated_mask_image;

          const segCount = Number(segJson.leaves_detected);
          leavesDetected = Number.isFinite(segCount) ? segCount : null;

          // Call old classification API
          finalResult = await ApiService.post('/diagnose-leaf', formData, true);
          window.dispatchEvent(new CustomEvent('credit-deducted', { detail: { amount: 5 } }));
        }

        setItems(prev => prev.map((item, idx) => idx === i ? {
          ...item, status: 'success', result: finalResult, glowImage
        } : item));

        outcomes.push({ ok: true, file: sourceFile, result: finalResult, glowImage, leavesDetected, error: null });

      } catch (e) {
        setItems(prev => prev.map((item, idx) => idx === i ? {
          ...item, status: 'error', errorMsg: e.message || t.leaf.noLeaves
        } : item));

        outcomes.push({ ok: false, file: sourceFile, result: null, glowImage: null, leavesDetected: null, error: e.message || t.leaf.noLeaves });
      }
    }

    await commitLeafReport(outcomes);
    setIsProcessing(false);
  };

  // The legacy single-image `uploadAndDiagnose` handler and its state (file / preview / result /
  // loading) were removed: nothing rendered ever called them, and its bare setReportData wrote
  // leaf_diagnosis with no run marker and no input image. processImages is the live path.

  // --- SMART TRANSLATION HELPER ---
  const getTranslatedDisease = (rawName) => {
      if (!rawName) return "";
      
      // Safety check: ensure diseases object exists
      const diseases = t.leaf?.diseases || {};

      // 1. Try Direct Lookup (e.g. "Tomato___Late_blight")
      if (diseases[rawName]) return diseases[rawName];

      // 2. Try "Re-Snaking" the key
      // If backend sends "Tomato - Late Blight", we convert it back to "Tomato___Late_Blight"
      // to find it in our dictionary.
      const reSnaked = rawName.replace(/ - /g, '___').replace(/ /g, '_');
      if (diseases[reSnaked]) return diseases[reSnaked];
      
      // 3. Try removing extra spaces just in case
      const simpleSnake = rawName.replace(/ /g, '_');
      if (diseases[simpleSnake]) return diseases[simpleSnake];

      // 4. Fallback: Return raw English name if no match found
      return rawName;
  };

  return (
    <div className="h-full flex flex-col max-w-7xl mx-auto font-sans text-[#1B263B] dark:text-white">
      
      {/* Header & Controls */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between w-full mb-8">
        <div>
           <h2 className="text-3xl font-bold flex items-center gap-3 text-[#1B263B] dark:text-white">
             <Leaf className="text-[#6B8F71]" size={32} /> {t.leaf?.uploadTitle || "AI Leaf Doctor"}
           </h2>
           <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Upload leaf images for deep segmentation and disease diagnosis.</p>
        </div>
        
        {/* Plant Type Toggle (Pill shape) */}
        <div className="flex bg-white dark:bg-slate-800 rounded-full p-1 border border-[#1B263B]/10 dark:border-slate-700 shadow-sm mt-4 md:mt-0 transition-colors">
          <button onClick={() => setPlantType('general')} className={`px-6 py-2 rounded-full font-medium transition-all ${plantType === 'general' ? 'bg-[#1B263B] dark:bg-[#6B8F71] text-white shadow-md' : 'text-gray-500 dark:text-gray-400 hover:text-[#1B263B] dark:hover:text-white'}`}>
            {t.leaf?.typeGeneral || "General"}
          </button>
          <button onClick={() => setPlantType('palm')} className={`px-6 py-2 rounded-full font-medium transition-all ${plantType === 'palm' ? 'bg-[#1B263B] dark:bg-[#6B8F71] text-white shadow-md' : 'text-gray-500 dark:text-gray-400 hover:text-[#1B263B] dark:hover:text-white'}`}>
            {t.leaf?.typePalm || "Palm"}
          </button>
        </div>
      </div>

      {/* Split Pane Layout */}
      <div className="flex flex-col lg:flex-row gap-8 w-full flex-1">
          
          {/* LEFT PANE (INPUT) */}
          <div className="w-full lg:w-1/3 flex flex-col">
              <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 border border-[#1B263B]/5 dark:border-slate-700 shadow-sm flex-1 flex flex-col transition-colors">
                  <h3 className="font-bold text-lg mb-4 text-[#1B263B] dark:text-white">Input Imagery</h3>
                  
                  <div className="relative w-full flex-1 min-h-[300px] border-2 border-dashed border-[#6B8F71]/50 bg-[#6B8F71]/5 hover:bg-[#6B8F71]/10 dark:bg-slate-900 dark:hover:bg-slate-800 rounded-xl transition-colors cursor-pointer flex flex-col items-center justify-center group overflow-hidden">
                      <input type="file" multiple onChange={handleFileChange} className="absolute inset-0 opacity-0 cursor-pointer z-20" accept="image/*" />
                      <div className="p-4 bg-white rounded-full mb-3 shadow-md group-hover:scale-110 transition-transform">
                          <Upload size={32} className="text-[#6B8F71]" />
                      </div>
                      <p className="text-lg text-[#1B263B] dark:text-white font-medium text-center px-4">{t.leaf?.dropText || "Drag & drop images here"}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Supports JPG, PNG up to 10MB</p>
                  </div>
                  
                  {items.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-[#1B263B]/10 dark:border-slate-700 flex gap-3">
                          <button onClick={handleReset} className="flex-1 px-4 py-3 text-gray-500 dark:text-gray-400 hover:text-[#1B263B] dark:hover:text-white font-medium border border-gray-200 dark:border-slate-600 rounded-xl hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors">
                              {t.leaf?.cancel || "Clear"}
                          </button>
                          <button onClick={processImages} disabled={isProcessing} className="flex-[2] px-6 py-3 bg-[#C47A2C] hover:bg-[#A36322] text-white rounded-xl font-bold shadow-lg shadow-[#C47A2C]/30 transition-transform transform hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0">
                              {isProcessing ? (t.leaf?.processing || 'Processing...') : (t.leaf?.btnAnalyze || 'Analyze Images')}
                          </button>
                      </div>
                  )}
              </div>
          </div>

          {/* RIGHT PANE (OUTPUT) */}
          <div className="w-full lg:w-2/3 flex flex-col gap-6">
              {items.length === 0 ? (
                  /* Output Skeleton Loader Empty State */
                  <div className="w-full h-full min-h-[400px] bg-white/50 dark:bg-slate-800/50 rounded-2xl border border-[#1B263B]/5 dark:border-slate-700 shadow-sm flex flex-col items-center justify-center p-8 opacity-70 transition-colors">
                      <div className="w-24 h-24 rounded-full bg-gray-200 dark:bg-slate-700 animate-pulse mb-6"></div>
                      <div className="w-64 h-6 bg-gray-200 dark:bg-slate-700 rounded-full animate-pulse mb-3"></div>
                      <div className="w-48 h-4 bg-gray-200 dark:bg-slate-700 rounded-full animate-pulse"></div>
                      <p className="text-gray-400 dark:text-gray-500 mt-8 text-sm">Upload an image to see the diagnosis results here.</p>
                  </div>
              ) : (
                  <div className="flex flex-col gap-6 overflow-y-auto pr-2 custom-scrollbar max-h-[80vh]">
                      {items.map((item, idx) => (
                          <div key={item.id} className="bg-white dark:bg-slate-800 rounded-2xl p-6 border border-[#1B263B]/5 dark:border-slate-700 shadow-sm relative overflow-hidden transition-colors flex flex-col gap-5 shrink-0">
                              
                              {/* The "Scanning" Effect (Copper Laser) */}
                              {item.status === 'processing' && (
                                  <div className="absolute inset-0 z-50 pointer-events-none rounded-2xl overflow-hidden border border-[#C47A2C]/30 shadow-[0_0_15px_rgba(196,122,44,0.2)_inset]">
                                      <div className="absolute top-0 left-0 w-full h-[2px] bg-[#C47A2C] shadow-[0_5px_15px_rgba(196,122,44,0.8)] animate-scan-laser line-gradient-bottom"></div>
                                      <div className="absolute inset-0 bg-[#C47A2C]/5 animate-pulse"></div>
                                  </div>
                              )}

                              {/* Header: Results / Status */}
                              <div className="flex justify-between items-center bg-gray-50 dark:bg-slate-900/50 p-4 rounded-xl border border-[#1B263B]/5 dark:border-slate-700/50 relative z-10 transition-colors">
                                  {item.status === 'success' && item.result ? (
                                      <div className="flex justify-between w-full items-center">
                                          <h3 className="text-xl font-bold text-[#1B263B] dark:text-[#F4EFEA]">{item.result.diagnosis}</h3>
                                          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                                              <span>{t.leaf?.confidence || 'Confidence'}:</span>
                                              <span className="font-mono font-bold text-[#6B8F71]">{item.result.confidence}%</span>
                                          </div>
                                      </div>
                                  ) : item.status === 'error' ? (
                                      <span className="text-red-500 font-bold flex items-center gap-2"><X size={18} /> {item.errorMsg}</span>
                                  ) : item.status === 'processing' ? (
                                      <span className="text-[#C47A2C] font-mono tracking-widest uppercase flex items-center gap-2 animate-pulse"><Activity size={18} /> {t.leaf?.processing || 'Deep Scanning...'}</span>
                                  ) : (
                                      <span className="text-gray-400 italic text-sm">{t.leaf?.pending || "Awaiting submission..."}</span>
                                  )}
                              </div>

                              {/* Side-by-Side Images Container (Uncropped, Full Res) */}
                              <div className="flex flex-col sm:flex-row items-center gap-4 w-full relative z-10">
                                  
                                  {/* Left: Input Image */}
                                  <a href={item.preview} target="_blank" rel="noopener noreferrer" className="w-full sm:w-5/12 cursor-zoom-in relative group" title="Click to view full size">
                                      <img src={item.preview} alt="Input" className="w-full h-auto rounded-xl border border-[#1B263B]/10 dark:border-slate-700 group-hover:border-[#6B8F71] dark:group-hover:border-[#6B8F71] transition-colors shadow-sm" />
                                  </a>

                                  {/* Middle: Arrow/Status */}
                                  <div className="flex flex-col items-center justify-center shrink-0 w-full sm:w-2/12">
                                       {item.status === 'processing' ? (
                                          <div className="w-8 h-8 border-4 border-[#C47A2C] border-t-transparent rounded-full animate-spin shadow-[0_0_15px_rgba(196,122,44,0.3)]"></div>
                                       ) : item.status === 'success' ? (
                                          <>
                                             <ArrowRight size={32} className="text-[#6B8F71] hidden sm:block drop-shadow-[0_0_10px_rgba(107,143,113,0.5)]" />
                                             <ArrowDown size={32} className="text-[#6B8F71] block sm:hidden" />
                                          </>
                                       ) : item.status === 'error' ? (
                                          <X size={32} className="text-red-500" />
                                       ) : (
                                          <span className="text-xs text-gray-400 tracking-wider uppercase">—</span>
                                       )}
                                  </div>

                                  {/* Right: Output Image (Glowing Overlay) */}
                                  <div className="w-full sm:w-5/12 flex items-center justify-center min-h-[160px] bg-[#1B263B]/5 dark:bg-slate-900/40 rounded-xl border border-[#1B263B]/10 dark:border-slate-700 relative overflow-hidden transition-colors">
                                      {item.glowImage ? (
                                          <a href={item.glowImage} onClick={(e) => openImageInNewTab(e, item.glowImage)} target="_blank" rel="noopener noreferrer" className="relative w-full h-full flex items-center justify-center cursor-zoom-in group" title="Click to view full mask">
                                              <img src={item.preview} alt="Background" className="absolute inset-0 w-full h-full object-contain opacity-20 grayscale transition-opacity duration-300 group-hover:opacity-40" />
                                              <img src={item.glowImage} alt="Segmented Output" className="relative z-10 w-full h-full object-contain drop-shadow-[0_0_20px_rgba(107,143,113,1)] transition-transform duration-300 group-hover:scale-105" />
                                          </a>
                                      ) : item.status === 'error' ? (
                                          <span className="text-red-500/50 text-sm font-medium">No Output</span>
                                      ) : (
                                          <span className="text-gray-400/50 dark:text-slate-600 text-sm font-medium">Awaiting Output</span>
                                      )}
                                  </div>
                              </div>
                          </div>
                      ))}
                  </div>
              )}
          </div>
      </div>
    </div>
  );
};

export default LeafDoctorView;
